/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Icon } from './ComponentIcon';
import {
    normalizePageSize,
    PAGE_SIZE_OPTIONS,
} from '../../config/pagination.config';
import './Pagination.css';

export interface PaginationLabels {
    perPage: string;
    firstPage: string;
    previousPage: string;
    nextPage: string;
    lastPage: string;
}

export interface PaginationViewProps {
    currentPage: number;
    totalPages: number;
    pageSize: number;
    totalItems: number;
    onPageChange: (page: number) => void;
    onPageSizeChange?: (pageSize: number) => void;
    /** @deprecated Kurumsal seçenekler PAGE_SIZE_OPTIONS ile merkezi olarak uygulanır. */
    pageSizeOptions?: readonly number[];
    /** Labels come from the host locale; the renderer has no auth or API dependency. */
    labels: PaginationLabels;
    showPageSizeSelect?: boolean;
}

export const PaginationView: React.FC<PaginationViewProps> = ({
    currentPage,
    totalPages,
    pageSize,
    totalItems,
    onPageChange,
    onPageSizeChange,
    labels,
    showPageSizeSelect = true
}) => {
    const [pageSizeOpen, setPageSizeOpen] = useState(false);
    const pageSizeRef = useRef<HTMLDivElement>(null);
    const normalizedRequestRef = useRef<number | null>(null);
    const normalizedPageSizeOptions = [...PAGE_SIZE_OPTIONS];
    // Read-only/server-owned page sizes must not silently change the displayed range.
    const resolvedPageSize = onPageSizeChange ? normalizePageSize(pageSize) : Math.max(1, pageSize);
    const canChangePageSize = Boolean(onPageSizeChange) && showPageSizeSelect && normalizedPageSizeOptions.length > 1;

    useEffect(() => {
        if (pageSize === resolvedPageSize) {
            normalizedRequestRef.current = null;
            return;
        }
        if (normalizedRequestRef.current === resolvedPageSize) return;
        normalizedRequestRef.current = resolvedPageSize;
        onPageSizeChange?.(resolvedPageSize);
    }, [onPageSizeChange, pageSize, resolvedPageSize]);

    useEffect(() => {
        if (!pageSizeOpen) return;
        pageSizeRef.current?.querySelector<HTMLElement>('[role="option"][aria-selected="true"]')?.focus();
        const handleClickOutside = (e: MouseEvent) => {
            if (pageSizeRef.current && !pageSizeRef.current.contains(e.target as Node)) {
                setPageSizeOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [pageSizeOpen]);

    // Sayfalama metinlerini hesapla
    const startItem = (currentPage - 1) * resolvedPageSize + 1;
    const endItem = Math.min(currentPage * resolvedPageSize, totalItems);
    const paginationRangeText = totalItems > 0
        ? `${startItem}-${endItem} / ${totalItems}`
        : '0-0 / 0';
    const paginationPageText = `${currentPage} / ${totalPages || 1}`;

    return (
        <div className="pagination-shell">
            <div className="products-pagination">
                <div className="pagination-info">
                    <span className="pagination-range"><bdi dir="ltr">{paginationRangeText}</bdi></span>
                    {canChangePageSize && (
                        <div className="page-size-select-wrap" ref={pageSizeRef}>
                            <button
                                type="button"
                                className="pagination-page-size-select page-size-select"
                                aria-label={labels.perPage}
                                aria-expanded={pageSizeOpen}
                                aria-haspopup="listbox"
                                onClick={() => setPageSizeOpen((o) => !o)}
                            >
                                <span className="page-size-select-value">{resolvedPageSize}</span>
                                <Icon name="expandMore" size={16} className="page-size-select-chevron" />
                            </button>
                            {pageSizeOpen && (
                                <ul
                                    className="page-size-select-dropdown"
                                    role="listbox"
                                    aria-label={labels.perPage}
                                    onKeyDown={(event) => {
                                        const options = [...event.currentTarget.querySelectorAll<HTMLElement>('[role="option"]')];
                                        const index = options.indexOf(document.activeElement as HTMLElement);
                                        if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
                                            event.preventDefault();
                                            const next = event.key === 'Home' ? 0 : event.key === 'End' ? options.length - 1
                                                : (index + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
                                            options[next]?.focus();
                                        } else if (event.key === 'Escape' || event.key === 'Tab') {
                                            setPageSizeOpen(false);
                                            if (event.key === 'Escape') {
                                                event.preventDefault();
                                                event.stopPropagation();
                                                pageSizeRef.current?.querySelector('button')?.focus();
                                            }
                                        }
                                    }}
                                >
                                    {normalizedPageSizeOptions.map((option) => (
                                        <li
                                            key={option}
                                            role="option"
                                            tabIndex={-1}
                                            aria-selected={resolvedPageSize === option}
                                            className={`page-size-option ${resolvedPageSize === option ? 'selected' : ''}`}
                                            onClick={() => {
                                                onPageSizeChange?.(option);
                                                setPageSizeOpen(false);
                                                pageSizeRef.current?.querySelector('button')?.focus();
                                            }}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter' || event.key === ' ') {
                                                    event.preventDefault();
                                                    event.currentTarget.click();
                                                }
                                            }}
                                        >
                                            {option}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
                <div className="pagination-controls">
                    <button
                        type="button"
                        className="pagination-btn"
                        onClick={() => onPageChange(1)}
                        disabled={currentPage === 1}
                        title={labels.firstPage}
                        aria-label={labels.firstPage}
                    >
                        <Icon name="chevronLeft" size={16} />
                        <Icon name="chevronLeft" size={16} />
                    </button>
                    <button
                        type="button"
                        className="pagination-btn"
                        onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        title={labels.previousPage}
                        aria-label={labels.previousPage}
                    >
                        <Icon name="chevronLeft" size={16} />
                    </button>
                    <span className="pagination-page"><bdi dir="ltr">{paginationPageText}</bdi></span>
                    <button
                        type="button"
                        className="pagination-btn"
                        onClick={() => onPageChange(Math.min(totalPages || 1, currentPage + 1))}
                        disabled={currentPage === totalPages || totalPages === 0}
                        title={labels.nextPage}
                        aria-label={labels.nextPage}
                    >
                        <Icon name="chevronRight" size={16} />
                    </button>
                    <button
                        type="button"
                        className="pagination-btn"
                        onClick={() => onPageChange(totalPages || 1)}
                        disabled={currentPage === totalPages || totalPages === 0}
                        title={labels.lastPage}
                        aria-label={labels.lastPage}
                    >
                        <Icon name="chevronRight" size={16} />
                        <Icon name="chevronRight" size={16} />
                    </button>
                </div>
            </div>
        </div>
    );
};
